import {
  AiActionCapabilityV1Schema,
  AiActionIdV1Schema,
  type AiActionPolicyV1,
  AiActionPolicyV1Schema,
} from "@adpilot/shared";
import { z } from "zod";
import { api } from "./api";

const operationStatusSchema = z.enum([
  "pending_approval",
  "approved",
  "rejected",
  "cancelled",
  "queued",
  "executing",
  "succeeded",
  "failed",
  "partial_success",
  "reconciliation_required",
]);

const stepStatusSchema = z.enum(["pending", "executing", "succeeded", "failed", "result_unknown"]);

const eventTypeSchema = z.enum([
  "accepted",
  "approved",
  "rejected",
  "cancelled",
  "queued",
  "step_started",
  "step_succeeded",
  "step_failed",
  "step_result_unknown",
]);

const timestampSchema = z.string().datetime();

const actionOperationDetailSchema = z.object({
  id: z.string().uuid(),
  shopId: z.string().uuid(),
  status: operationStatusSchema,
  version: z.number().int().positive(),
  // Keep action-specific payloads intact; the record view validates reviewed
  // display whitelists before presenting evidence or planned changes.
  proposal: z
    .object({
      version: z.literal(1),
      action: AiActionIdV1Schema,
      campaignIds: z.array(z.string().uuid()),
    })
    .passthrough(),
  decidedBy: z.string().uuid().nullable(),
  decidedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  executionEnabled: z.boolean().default(false),
  steps: z.array(
    z.object({
      id: z.string().uuid(),
      operationId: z.string().uuid(),
      ordinal: z.number().int().positive(),
      campaignId: z.string().uuid(),
      resourceKey: z.string(),
      plannedChange: z.record(z.string(), z.unknown()),
      status: stepStatusSchema,
      startedAt: timestampSchema.nullable(),
      finishedAt: timestampSchema.nullable(),
    }),
  ),
  events: z.array(
    z.object({
      id: z.string().uuid(),
      operationId: z.string().uuid(),
      operationVersion: z.number().int().positive(),
      stepOrdinal: z.number().int().positive().nullable(),
      eventType: eventTypeSchema,
      detail: z.record(z.string(), z.unknown()),
      createdAt: timestampSchema,
    }),
  ),
});

const responseSchema = z.object({
  success: z.literal(true),
  data: actionOperationDetailSchema,
});

export interface ActionOperationDetail
  extends Omit<z.infer<typeof actionOperationDetailSchema>, "executionEnabled"> {
  executionEnabled?: boolean;
}

export async function fetchActionOperationDetail(
  operationId: string,
  shopId: string,
): Promise<ActionOperationDetail> {
  const response: unknown = await api
    .get(`ai-action-operations/${operationId}`, { headers: { "X-Shop-Id": shopId } })
    .json();
  const detail = responseSchema.parse(response).data;
  if (detail.id !== operationId || detail.shopId !== shopId) {
    throw new Error("Action operation scope mismatch");
  }
  return detail;
}

const commandSchema = z.object({ expectedVersion: z.number().int().positive() }).strict();
const decisionSchema = commandSchema.extend({ decision: z.enum(["approve", "reject"]) }).strict();
const commandResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    id: z.string().uuid(),
    status: operationStatusSchema,
    version: z.number().int().positive(),
    updatedAt: timestampSchema,
  }),
});

async function operationCommand(operationId: string, shopId: string, path: string, body: unknown) {
  z.string().uuid().parse(operationId);
  z.string().uuid().parse(shopId);
  const response: unknown = await api
    .post(`ai-action-operations/${operationId}/${path}`, {
      headers: { "X-Shop-Id": shopId },
      json: body,
    })
    .json();
  const result = commandResponseSchema.parse(response).data;
  if (result.id !== operationId) throw new Error("Action operation scope mismatch");
  return result;
}

export function decideActionOperation(
  operationId: string,
  shopId: string,
  input: z.input<typeof decisionSchema>,
) {
  return operationCommand(operationId, shopId, "decisions", decisionSchema.parse(input));
}
export function cancelActionOperation(
  operationId: string,
  shopId: string,
  input: z.input<typeof commandSchema>,
) {
  return operationCommand(operationId, shopId, "cancellation", commandSchema.parse(input));
}
export function executeActionOperation(
  operationId: string,
  shopId: string,
  input: z.input<typeof commandSchema>,
) {
  return operationCommand(operationId, shopId, "execution", commandSchema.parse(input));
}
export function reconcileActionOperation(
  operationId: string,
  shopId: string,
  input: z.input<typeof commandSchema>,
) {
  return operationCommand(operationId, shopId, "reconciliation", commandSchema.parse(input));
}

const policyResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    policy: AiActionPolicyV1Schema.nullable(),
    executionEnabled: z.boolean().default(false),
  }),
});
const scopeSchema = z.object({ campaignId: z.string().uuid(), shopId: z.string().uuid() });

function assertPolicyScope(policy: AiActionPolicyV1 | null, campaignId: string, shopId: string) {
  if (
    policy &&
    (policy.source !== "campaign" || policy.campaignId !== campaignId || policy.shopId !== shopId)
  ) {
    throw new Error("Campaign action policy scope mismatch");
  }
}

export async function fetchCampaignActionPolicy(campaignId: string, shopId: string) {
  scopeSchema.parse({ campaignId, shopId });
  const response: unknown = await api
    .get(`campaigns/${campaignId}/ai-action-policy`, {
      headers: { "X-Shop-Id": shopId },
    })
    .json();
  const result = policyResponseSchema.parse(response).data;
  assertPolicyScope(result.policy, campaignId, shopId);
  return result;
}

export async function fetchCampaignActionCapabilities(campaignId: string, shopId: string) {
  scopeSchema.parse({ campaignId, shopId });
  const response: unknown = await api
    .get(`campaigns/${campaignId}/ai-action-capabilities`, {
      headers: { "X-Shop-Id": shopId },
      retry: 0,
    })
    .json();
  const result = z
    .object({
      success: z.literal(true),
      data: scopeSchema
        .extend({
          capabilities: z.array(AiActionCapabilityV1Schema),
        })
        .strict(),
    })
    .parse(response).data;
  if (result.shopId !== shopId || result.campaignId !== campaignId)
    throw new Error("Campaign action capability scope mismatch");
  return result;
}

export async function updateCampaignActionPolicy(
  campaignId: string,
  shopId: string,
  input: {
    expectedRevision: number;
    policy: AiActionPolicyV1;
  },
) {
  scopeSchema.parse({ campaignId, shopId });
  const body = z
    .object({ expectedRevision: z.number().int().nonnegative(), policy: AiActionPolicyV1Schema })
    .strict()
    .parse(input);
  assertPolicyScope(body.policy, campaignId, shopId);
  if (body.policy.revision !== body.expectedRevision + 1)
    throw new Error("Campaign action policy revision mismatch");
  const response: unknown = await api
    .put(`campaigns/${campaignId}/ai-action-policy`, {
      headers: { "X-Shop-Id": shopId },
      json: body,
    })
    .json();
  const result = policyResponseSchema.parse(response).data;
  assertPolicyScope(result.policy, campaignId, shopId);
  if (!result.policy || result.policy.revision !== body.policy.revision)
    throw new Error("Campaign action policy revision mismatch");
  return result;
}

export const proposalActionSchema = z.enum([
  "pauseCampaign",
  "placementBid",
  "budgetBoost",
  "harvest",
  "negateSource",
  "negation",
  "pauseAndSupplement",
]);
export type ProposalAction = z.infer<typeof proposalActionSchema>;
const operationSummarySchema = actionOperationDetailSchema.pick({
  id: true,
  shopId: true,
  status: true,
  version: true,
  proposal: true,
  createdAt: true,
  updatedAt: true,
});
const proposalResponseSchema = z.object({
  success: z.literal(true),
  data: z.discriminatedUnion("status", [
    z.object({
      status: z.literal("approval_required"),
      operations: z.array(operationSummarySchema),
    }),
    z.object({ status: z.literal("unavailable"), reason: z.string().min(1).max(200) }),
  ]),
});

export async function proposeCampaignAction(
  campaignId: string,
  shopId: string,
  input: {
    action: ProposalAction;
    idempotencyKey: string;
  },
) {
  scopeSchema.parse({ campaignId, shopId });
  const body = z
    .object({ action: proposalActionSchema, idempotencyKey: z.string().trim().min(1).max(150) })
    .strict()
    .parse(input);
  const response: unknown = await api
    .post(`campaigns/${campaignId}/ai-action-proposals`, {
      headers: { "X-Shop-Id": shopId },
      json: body,
    })
    .json();
  const result = proposalResponseSchema.parse(response).data;
  if (
    result.status === "approval_required" &&
    result.operations.some(
      (operation) =>
        operation.shopId !== shopId ||
        !operation.proposal.campaignIds.includes(campaignId) ||
        operation.proposal.action !== body.action,
    )
  ) {
    throw new Error("Action operation scope mismatch");
  }
  return result;
}

export async function fetchCampaignActionOperations(campaignId: string, shopId: string, page = 1) {
  scopeSchema.parse({ campaignId, shopId });
  z.number().int().positive().parse(page);
  const response: unknown = await api
    .get("ai-action-operations", {
      headers: { "X-Shop-Id": shopId },
      searchParams: { campaignId, page, pageSize: 10 },
    })
    .json();
  const result = z
    .object({
      success: z.literal(true),
      data: z.array(operationSummarySchema),
      pagination: z.object({
        page: z.number().int().positive(),
        pageSize: z.number().int().positive(),
        total: z.number().int().nonnegative(),
        hasMore: z.boolean(),
      }),
    })
    .parse(response);
  if (
    result.data.some(
      (operation) =>
        operation.shopId !== shopId || !operation.proposal.campaignIds.includes(campaignId),
    )
  )
    throw new Error("Action operation scope mismatch");
  if (result.pagination.page !== page) throw new Error("Action operation pagination mismatch");
  return result;
}
